import React from 'react';
import { H1 } from '../../components/elements/Text';
import Navbar from '../../components/Navbar';
import PageWithAsyncData from '../../components/PageWithAsyncData';
import styled from 'styled-components';
import { useGetCompany } from '../../hooks/use-hydra/admin';

import { useParams } from 'react-router-dom';
import Button from '../../components/elements/Button';
import day from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Cohort from '../../components/Admin/Cohort';
import AddCohortModal from '../../components/Admin/Modals/AddCohortModal';
import { COMPANY_PAGES } from '../../components/Admin/CompanySideNavigation';
import CompanyPageWrapper from '../../components/Admin/CompanyPageWrapper';
import {
  FiltersHorizontalWrapper,
  TextFilterPill,
  MultiSelectFilterPill,
  TextOrEmptyFilterPill,
  DateRangeFilterPill,
} from '../../components/elements/Filter';
import { CompanyResponse, LEARNER_STATUS } from '../../api/admin';
import dayjs from 'dayjs';
import { ROUTES } from '../../routing';
day.extend(utc);

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const NavbarWithBreadcrumbs = ({
  name,
  id,
}: {
  name?: string;
  id?: string;
}) => (
  <Navbar
    breadCrumbs={[
      {
        label: 'Home',
        url: ROUTES.HOME.getPath(),
      },
      {
        label: 'Teams',
        url: ROUTES.ADMIN.COMPANIES.getPath(),
      },
      ...(name && id
        ? [
            {
              label: name,
              url: ROUTES.ADMIN.COMPANY.getPath({ companyId: id }),
            },
          ]
        : []),
      { label: 'Cohorts', url: null },
    ]}
  />
);

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 32px;
  margin-bottom: 72px;
`;

const AddCohortButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <Button
      style={{ marginTop: '0px' }}
      type="secondary"
      onClick={onClick}
      label="Add Cohort"
      showIconBeforeLabel
      faIcon="fa-solid fa-plus"
    />
  );
};

export default function Cohorts() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error, refetch } = useGetCompany(parseInt(id));
  const [isAddCohortModalOpen, setIsAddCohortModalOpen] = React.useState(false);

  const [learnerName, setLearnerName] = React.useState('');
  const [selectedStatuses, setSelectedStatuses] = React.useState<
    LEARNER_STATUS[]
  >([]);
  const [mentorName, setMentorName] = React.useState<string | null>('');
  const [startDate, setStartDate] = React.useState('');
  const [endDate, setEndDate] = React.useState('');

  const STATUS_OPTIONS = [
    { label: 'Invited', value: LEARNER_STATUS.INVITED },
    { label: 'Onboarded', value: LEARNER_STATUS.ONBOARDED },
    { label: 'Active', value: LEARNER_STATUS.ACTIVE },
    { label: 'Inactive', value: LEARNER_STATUS.INACTIVE },
    { label: 'Invite Expired', value: LEARNER_STATUS.INVITE_EXPIRED },
    { label: 'Cohort Ended', value: LEARNER_STATUS.COHORT_ENDED },
  ];

  const filterLearners = (
    learners: CompanyResponse['cohorts'][number]['learners']
  ) => {
    let filtered = learners;
    if (selectedStatuses.length > 0) {
      filtered = filtered.filter((learner) =>
        selectedStatuses.includes(learner.status)
      );
    }
    if (learnerName.trim()) {
      const search = learnerName.trim().toLowerCase();
      filtered = filtered.filter((learner) =>
        learner.name.toLowerCase().includes(search)
      );
    }
    if (mentorName === null || mentorName?.trim()) {
      const search = mentorName?.trim().toLowerCase() ?? '';
      filtered = filtered.filter((learner) => {
        const hasMentor = learner.mentor && learner.mentor.name;
        const matchesMentor =
          mentorName?.trim() &&
          hasMentor &&
          learner.mentor?.name?.toLowerCase().includes(search);
        const isNoMentor =
          mentorName === null && (!learner.mentor || !learner.mentor.name);
        return matchesMentor || isNoMentor;
      });
    }
    if (startDate || endDate) {
      filtered = filtered.filter((learner) => {
        const lastActiveAt = learner.lastActiveAt;
        if (!lastActiveAt) return false;

        if (startDate && endDate) {
          return (
            dayjs.utc(lastActiveAt).isAfter(dayjs.utc(startDate)) &&
            dayjs.utc(lastActiveAt).isBefore(dayjs.utc(endDate))
          );
        }

        if (startDate) {
          return dayjs.utc(lastActiveAt).isAfter(dayjs.utc(startDate));
        }

        if (endDate) {
          return dayjs.utc(lastActiveAt).isBefore(dayjs.utc(endDate));
        }

        return true;
      });
    }

    return filtered;
  };

  return (
    <PageWithAsyncData
      isLoading={isLoading}
      error={error}
      retry={refetch}
      noScroll
    >
      <NavbarWithBreadcrumbs name={data?.name} id={id} />
      <CompanyPageWrapper currentPage={COMPANY_PAGES.COHORTS}>
        <H1>{data?.name} Cohorts</H1>
        <Content>
          <Row>
            <AddCohortButton onClick={() => setIsAddCohortModalOpen(true)} />
          </Row>
          <FiltersHorizontalWrapper style={{ marginTop: '36px' }}>
            <TextOrEmptyFilterPill
              label="Mentor"
              nullLabel="No mentor assigned"
              value={mentorName}
              placeholder="Type mentor name..."
              onChange={setMentorName}
            />
            <TextFilterPill
              label="Learner"
              value={learnerName}
              placeholder="Type the learner name..."
              onChange={setLearnerName}
            />
            <MultiSelectFilterPill
              label="Status"
              values={selectedStatuses}
              options={STATUS_OPTIONS}
              onChange={(values) =>
                setSelectedStatuses(values as LEARNER_STATUS[])
              }
            />

            <DateRangeFilterPill
              label="Last Active At"
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
            />
          </FiltersHorizontalWrapper>
          <CohortsWrapper>
            {data?.cohorts.map((cohort, index) => (
              <Cohort
                isFirstCohort={index === 0}
                key={cohort.cycleId}
                cohort={{
                  ...cohort,
                  learners: filterLearners(cohort.learners),
                }}
                companyId={parseInt(id)}
                allCompanyLearners={data?.learners ?? []}
              />
            ))}
          </CohortsWrapper>
        </Content>
      </CompanyPageWrapper>
      <AddCohortModal
        isOpen={isAddCohortModalOpen}
        onClose={() => setIsAddCohortModalOpen(false)}
      />
    </PageWithAsyncData>
  );
}

const CohortsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;
